import React, {useState, useEffect, useRef, ChangeEvent, forwardRef, useImperativeHandle} from 'react'

import AddressActions from '../../../actions/AddressActions'
import {Input} from './index'
import {InputRef} from './Input.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import NewAddress from '../../addresses/NewAddress.js'
import {Address} from '../../../../types/types.js'

interface Suggestion extends Address {
    value: string
}

// Input marks the change events it fires for a chosen suggestion, so they can be told apart from typing
type ValueChangeEvent = ChangeEvent<HTMLInputElement> & {fromSuggestion?: boolean}

export interface AddressSearchProps extends FieldProps, InvalidProps {
    id?: string
    address?: Address
    showStreetOnly?: boolean
    blurOnChooseSuggestion?: boolean
    onChange?: (address: Address) => void
}

export type AddressSearchRef = FieldRef

const formatAddress = (address: Address | undefined, showStreetOnly?: boolean): string => {
    if (!address || !address.street) {
        return ''
    }

    if (showStreetOnly) {
        return address.street
    }

    return `${address.street} ${address.nr || ''}${address.addition || ''}, ${address.postalCode || ''} ${address.city || ''} ${address.country || ''}`
        .replace(/  +/g, ' ')
        .trim()
}

const AddressSearch = forwardRef<AddressSearchRef, AddressSearchProps>(function AddressSearch({
    id = '',
    label = 'Adres',
    info,
    disabled,
    readOnly,
    isInvalid,
    address,
    showStreetOnly,
    blurOnChooseSuggestion,
    onChange,
    style
}, ref) {
    const inputRef = useRef<InputRef | null>(null)

    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        setInvalid: (isInvalid = true) => inputRef.current?.setInvalid(isInvalid)
    }), [])

    // Read only and disabled never change the address
    const editable = !disabled && !readOnly

    const [value, setValue] = useState<string>(() => formatAddress(address, showStreetOnly))

    const [suggestions, setSuggestions] = useState<Suggestion[]>([])
    const timeoutId = useRef<number | null>(null)

    // Only the response to the latest request may set suggestions, so a slow older one can't replace newer results
    const requestId = useRef<number>(0)

    // The address is already filled in from the suggestion itself, so the value changes it causes
    // must not start a new autocomplete request or be written back to street on blur
    const chosenSuggestion = useRef<boolean>(false)

    // Depends on the formatted text instead of the address object, so a parent re-render that recreates
    // an unchanged address doesn't discard search text that hasn't been chosen yet
    const formattedAddress = formatAddress(address, showStreetOnly)

    useEffect(() => {
        setValue(formattedAddress)
    }, [formattedAddress])

    useEffect(() => () => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current)
        }
        // Also stops a pending response from setting suggestions after unmount
        requestId.current = -1
    }, [])

    const handleValueChange = (event: ValueChangeEvent) => {
        if (!editable) {
            return
        }

        const inputValue = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value

        if (timeoutId.current) {
            clearTimeout(timeoutId.current)
            timeoutId.current = null
        }

        if (event.fromSuggestion) {
            setValue(inputValue)
            return
        }

        chosenSuggestion.current = false

        timeoutId.current = window.setTimeout(() => autocomplete(inputValue), 500)

        setValue(inputValue)

        if (!inputValue) {
            // Only clear what this field shows, so name, contact details etc. entered elsewhere in the form are kept
            onChange?.(showStreetOnly ?
                {...address, street: ''} :
                {
                    ...(address || NewAddress()),
                    street: '',
                    street2: '',
                    nr: '',
                    addition: '',
                    postalCode: '',
                    city: '',
                    country: 'NL',
                    position: undefined
                })
        }
    }

    const handleBlur = () => {
        if (chosenSuggestion.current || !editable) {
            return
        }

        if (showStreetOnly) {
            onChange?.({
                ...address,
                street: value
            })
        }
    }

    const autocomplete = (inputValue: string) => {
        timeoutId.current = null
        const currentRequestId = ++requestId.current

        if (inputValue.length > 3) {
            if (showStreetOnly) {
                inputValue = formatAddress({...address, street: inputValue} as Address)
            }
            AddressActions.autocompleteSearch(inputValue, (err: Error | null, fetchedSuggestions: Suggestion[]) => {
                if (!err && fetchedSuggestions && currentRequestId === requestId.current) {
                    setSuggestions(fetchedSuggestions)
                }
            })
        } else if (!inputValue) {
            setSuggestions([])
        }
    }

    const handleChooseSuggestion = (event: ChangeEvent<HTMLInputElement>) => {
        if (!editable) {
            return
        }

        const suggestion = suggestions.find((sug) => sug.value === event.target.value)

        chosenSuggestion.current = true

        // Ignore a response still pending for the typed text
        requestId.current++

        // Close the list until the user types again, instead of keeping the suggestions that match what was just chosen
        setSuggestions([])

        if (suggestion) {
            onChange?.(suggestion)
        }
    }

    return (
        <Input
            ref={inputRef}
            id={`searchAddress${id}`}
            label={label}
            info={
                value && address?.showWarning ?
                    'Dit adres staat niet in onze database, controleer of dit adres geldig is.' :
                    info
            }
            infoIcon={value && address?.showWarning ? 'mdi mdi-alert' : undefined}
            disabled={disabled}
            readOnly={readOnly}
            value={value}
            suggestions={suggestions}
            onChange={handleValueChange}
            onBlur={handleBlur}
            onChooseSuggestion={handleChooseSuggestion}
            blurOnChooseSuggestion={blurOnChooseSuggestion}
            isInvalid={isInvalid}
            style={style}
        />
    )
})

export default AddressSearch
